import { mutationFields } from '@nedelnik/shared';

import {
  META_CURSOR,
  META_NUMBERS,
  applyLocalMutation,
  dropFromQueue,
  mergeIncoming,
  purgeRecords,
  forgetQuarantine,
  quarantine,
  queueLength,
  readMeta,
  readQuarantine,
  rollbackFields,
  takeBatch,
  takeNumber,
  writeMeta,
} from '../db/local.js';

import {
  isAuthError,
  isPermanentRequestError,
  isResyncRequired,
  isUnreachable,
} from './transport.js';

import type { MergeResult } from '../db/local.js';
import type { LocalDb, NumberLease, QuarantinedMutation } from '../db/schema.js';
import type { SyncStatus } from './status.js';
import type { Transport } from './transport.js';
import type { Clock, Entity, Mutation, MutationKind, Source, SyncRecord } from '@nedelnik/shared';

/**
 * Движок синхронизации.
 *
 * Правка применяется к локальной копии сразу и ложится в очередь; очередь
 * уходит пачкой через паузу; ответ сервера разбирается по видам ошибок; чужие
 * правки забираются `pull` и сливаются тем же LWW (разделы 5.2 и 5.3
 * архитектуры).
 *
 * Всё, что снаружи, — за интерфейсами: транспорт, часы, время, случайность.
 * Иначе оффлайн, повторы и встречные правки проверялись бы вручную и с
 * секундомером.
 */

const DEFAULTS = {
  debounceMs: 400,
  pollIntervalMs: 60_000,
  maxOps: 500,
  maxBytes: 1024 * 1024,
  backoffBaseMs: 1_000,
  backoffMaxMs: 60_000,
  pullLimit: 500,
  /** Ниже этого остатка номеров пора просить следующий блок. */
  numbersLowWater: 5,
};

/** Имя замка отправки. Одно на origin — в этом весь смысл. */
const SEND_LOCK = 'nedelnik-sync-push';

export interface MutationInput {
  entity: Entity;
  entityId: string;
  kind: MutationKind;
  fields?: Record<string, unknown>;
  source?: Source;
  /** Значения, нужные новой записи, но не входящие в правку: `user_id` настроек. */
  seed?: Partial<SyncRecord>;
}

export interface EngineOptions {
  db: LocalDb;
  transport: Transport;
  deviceId: string;
  clock: Clock;
  now?: () => number;
  /** Джиттер повторов. Подменяется в тестах, чтобы задержки были предсказуемы. */
  random?: () => number;
  isOnline?: () => boolean;
  onStatus?: (status: SyncStatus) => void;
  onChange?: (changes: MergeResult[]) => void;
  /**
   * Сервер сказал, что сессии больше нет. Движок на этом останавливается:
   * повторять бессмысленно, а показать надо экран входа.
   */
  onSignedOut?: () => void;
  /** Записи, откатившиеся из копии: сервер не принял правку, которая их завела. */
  onRemove?: (keys: { entity: Entity; key: string }[]) => void;
  debounceMs?: number;
  pollIntervalMs?: number;
  maxOps?: number;
  maxBytes?: number;
  backoffBaseMs?: number;
  backoffMaxMs?: number;
  numbersLowWater?: number;
}

export interface SyncEngine {
  start(): Promise<void>;
  stop(): void;
  /** Своя правка: применяется локально, уходит в очередь, планирует отправку. */
  mutate(input: MutationInput): Promise<MergeResult>;
  /** Отправить очередь немедленно, не дожидаясь паузы. */
  flush(): Promise<void>;
  /**
   * Сколько правок ещё не уехало.
   *
   * Спрашивают перед выходом: `forget` стирает локальную копию вместе с
   * очередью, и уехать оттуда после этого нечему.
   */
  pending(): Promise<number>;
  /** Забрать чужие правки начиная с текущего курсора. */
  pull(): Promise<void>;
  /** Звоночек из сокета: пришёл новый seq. */
  bump(seq: number): void;
  /** Полный снимок: путь для устаревшего курсора и первого запуска. */
  bootstrap(): Promise<void>;
  status(): SyncStatus;
  cursor(): number;
  /** Что сервер отказался принять и почему. */
  rejected(): Promise<QuarantinedMutation[]>;
  /**
   * Выбросить отвергнутые правки. Необратимо: своей версии этих записей у нас
   * уже нет — она откатилась в момент отказа.
   */
  forgetRejected(): Promise<void>;
  /** Номер для новой задачи из локального блока; undefined — блок кончился. */
  allocateNumber(): Promise<number | undefined>;
}

export function createSyncEngine(options: EngineOptions): SyncEngine {
  const {
    db,
    transport,
    deviceId,
    clock,
    now = () => Date.now(),
    random = () => Math.random(),
    isOnline = () => globalThis.navigator?.onLine ?? true,
    onStatus,
    onChange,
    onRemove,
    onSignedOut,
  } = options;

  const debounceMs = options.debounceMs ?? DEFAULTS.debounceMs;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULTS.pollIntervalMs;
  const maxOps = options.maxOps ?? DEFAULTS.maxOps;
  const maxBytes = options.maxBytes ?? DEFAULTS.maxBytes;
  const backoffBaseMs = options.backoffBaseMs ?? DEFAULTS.backoffBaseMs;
  const backoffMaxMs = options.backoffMaxMs ?? DEFAULTS.backoffMaxMs;
  const numbersLowWater = options.numbersLowWater ?? DEFAULTS.numbersLowWater;

  let cursor = 0;
  let lease: NumberLease | undefined;
  let status: SyncStatus = { kind: 'saved' };
  let attempt = 0;
  let stopped = false;
  /** Сессии больше нет: повторять нечего, пока человек не войдёт заново. */
  let signedOut = false;

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  // Одна цепочка на все сетевые операции: pull посреди flush и два flush разом
  // дают гонку за курсор и за очередь, а выигрыша не дают никакого.
  let chain: Promise<void> = Promise.resolve();

  function serialize(task: () => Promise<void>): Promise<void> {
    chain = chain.then(task, task);
    return chain;
  }

  function setStatus(next: SyncStatus): void {
    status = next;
    onStatus?.(next);
  }

  /**
   * Сессии нет. Останавливаемся и говорим об этом наружу: повтор здесь не
   * поможет ничем, а «оффлайн» было бы враньём — сервер отвечает.
   */
  function giveUpOnSession(): void {
    if (signedOut) return;
    signedOut = true;
    setStatus({ kind: 'signed-out' });
    onSignedOut?.();
  }

  async function idle(): Promise<void> {
    if (signedOut) return;

    const pending = await queueLength(db);
    if (pending > 0) {
      setStatus({ kind: 'saving' });
      return;
    }

    // «Сохранено» — только когда сервер принял всё. Отвергнутая правка живёт в
    // локальной копии и на экране, и молчать о ней значит показывать человеку
    // задачу, которой на сервере нет.
    const stuck = (await readQuarantine(db)).length;
    setStatus(stuck === 0 ? { kind: 'saved' } : { kind: 'rejected', count: stuck });
  }

  function scheduleFlush(): void {
    if (stopped || signedOut) return;
    if (debounceTimer !== undefined) clearTimeout(debounceTimer);

    // Пауза после последней правки, а не после первой: пока человек печатает,
    // отправлять каждое нажатие незачем.
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      void flush();
    }, debounceMs);
  }

  function scheduleRetry(pending: number, unreachable: boolean): void {
    if (stopped || signedOut) return;

    const delay = Math.min(backoffBaseMs * 2 ** attempt, backoffMaxMs);
    // Джиттер: без него все вкладки и все устройства возвращаются в сеть
    // одновременно и бьют в сервер одной волной.
    const jittered = Math.round(delay * (0.5 + random() * 0.5));
    attempt += 1;

    // «Нет связи» и «сервер ответил ошибкой» — разные новости, и различает их
    // сам отказ, а не `navigator.onLine`: тот остаётся `true`, когда у машины
    // есть подключение, но до сервера всё равно не достучаться.
    setStatus(
      unreachable || !isOnline()
        ? { kind: 'offline', pending }
        : { kind: 'error', pending, retryInSeconds: Math.max(1, Math.round(jittered / 1000)) },
    );

    if (retryTimer !== undefined) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      void flush();
    }, jittered);
  }

  // ------------------------------------------------------------------------
  // Отправка
  // ------------------------------------------------------------------------

  /**
   * Нужно ли восстановить копию снимком: ставится, когда что-то откатили.
   * Снимок берётся после разбора пачки, а не посреди неё, — иначе он пришёлся
   * бы на середину отправки и перемешался с ней.
   */
  let rollbackNeeded = false;

  async function sendBatch(ops: Mutation[], keys: number[]): Promise<void> {
    const wantNumbers = lease === undefined || lease.to - lease.next < numbersLowWater;

    const response = await transport.push({
      device_id: deviceId,
      ops,
      ...(wantNumbers ? { want_numbers: true } : {}),
    });

    const settled = new Set<string>();
    for (const item of response.applied) settled.add(item.op_id);

    if (response.rejected.length > 0) {
      const at = new Date(now()).toISOString();
      const byId = new Map(ops.map((op) => [op.op_id, op]));

      const entries: { mutation: Mutation; reason: string; at: string }[] = [];
      const removed: { entity: Entity; key: string }[] = [];
      const restored: MergeResult[] = [];

      for (const item of response.rejected) {
        const mutation = byId.get(item.op_id);
        settled.add(item.op_id);

        // Причина отказа пишется в консоль сразу: без неё остаётся только
        // «правка не доехала», а это самый бесполезный из возможных диагнозов.
        console.error(
          `[sync] сервер отверг ${item.op_id} (${mutation?.entity ?? '?'}): ${item.reason}`,
          mutation,
        );

        if (mutation === undefined) continue;
        entries.push({ mutation, reason: item.reason, at });

        // Ключ записи — `entity_id` мутации: у настроек это идентификатор
        // пользователя, у остальных — идентификатор самой записи.
        const key = mutation.entity_id;

        // Откат снимает часы с полей отвергнутой правки. Прежде вместо этого
        // удалялась вся запись: отказ по одному сроку уносил с экрана задачу
        // вместе с названием, чек-листом и тегами, а возвращал её только
        // снимок — не приехал снимок, и задачи больше нет.
        const rolled = await rollbackFields(
          db,
          mutation.entity,
          key,
          Object.keys(mutationFields(mutation)),
        );
        if (rolled === undefined) continue;

        // Часов не осталось вовсе — значит всё, что копия знала об этой
        // записи, принесла отвергнутая правка: сервер её не принял, и записи
        // нет ни у него, ни на других устройствах. Такую надо убрать, иначе на
        // экране останется задача, которую видит одна эта вкладка.
        //
        // Признак именно такой, а не `last_seq`: тот проставляется только
        // приходящим снимком, и у только что заведённой и успешно
        // отправленной задачи он ещё ноль — по нему она выглядела бы чужой
        // для сервера, хотя он её уже принял.
        if (Object.keys(rolled.record.field_clocks).length === 0) {
          removed.push({ entity: mutation.entity, key });
        } else {
          restored.push(rolled);
        }
      }

      await quarantine(db, entries);

      if (removed.length > 0) {
        await purgeRecords(db, removed);
        onRemove?.(removed);
      }
      if (restored.length > 0) onChange?.(restored);

      rollbackNeeded = true;
    }

    // Из очереди уходит только то, о судьбе чего сервер высказался. Мутация,
    // не попавшая ни в applied, ни в rejected, остаётся ждать следующей пачки.
    const done = keys.filter((_, index) => settled.has(ops[index]?.op_id ?? ''));
    await dropFromQueue(db, done);

    if (response.number_lease !== undefined) {
      lease = { next: response.number_lease.from_number, to: response.number_lease.to_number };
      await writeMeta(db, META_NUMBERS, lease);
    }

    // Курсор из push не берём. Он глобальный по пользователю и учитывает
    // правки других устройств, которых у нас ещё нет: приняв его за свой, мы
    // пропустили бы их навсегда.
  }

  /**
   * Отправка с делением пополам. Сервер проверяет пачку целиком, поэтому одна
   * негодная мутация валит все пятьсот; деление находит виноватую за log₂
   * запросов, вместо того чтобы отправлять остальных в карантин заодно с ней.
   */
  async function sendWithSplit(ops: Mutation[], keys: number[]): Promise<void> {
    try {
      await sendBatch(ops, keys);
    } catch (error) {
      if (!isPermanentRequestError(error)) throw error;

      if (ops.length === 1) {
        const mutation = ops[0];
        if (mutation !== undefined) {
          await quarantine(db, [
            {
              mutation,
              reason: error instanceof Error ? error.message : 'отклонена сервером',
              at: new Date(now()).toISOString(),
            },
          ]);
        }
        await dropFromQueue(db, keys);
        return;
      }

      const middle = Math.ceil(ops.length / 2);
      await sendWithSplit(ops.slice(0, middle), keys.slice(0, middle));
      await sendWithSplit(ops.slice(middle), keys.slice(middle));
    }
  }

  /**
   * Отправляет одна вкладка разом.
   *
   * Очередь лежит в IndexedDB и общая на origin, а `flush` зовёт каждая
   * вкладка — по своей паузе, по возврату в сеть, по своему таймеру. Две
   * вкладки брали из очереди **одну и ту же** пачку и отправляли её
   * одновременно; сервер принимал первую, а второй отвечал `duplicate`, и
   * правка уезжала в карантин при живой задаче на сервере. Серверную половину
   * этой гонки закрыли блокировкой счётчика, но чинить её там — лечить
   * следствие: посылать одно и то же дважды незачем.
   *
   * Замок берётся с `ifAvailable`, а не с ожиданием, и это важнее, чем
   * кажется. Ждущий замок связал бы вкладку с сетевым запросом соседней:
   * зависший запрос без таймаута остановил бы отправку во всём браузере.
   * А ждать и незачем — очередь общая, и сосед увезёт наши правки вместе со
   * своими. Единственный случай, который надо доиграть: сосед взял пачку
   * раньше, чем наша правка в неё попала. Ради него — повторная попытка.
   */
  async function withSendLock(run: () => Promise<void>): Promise<void> {
    const locks = globalThis.navigator?.locks;
    if (locks === undefined) {
      await run();
      return;
    }

    const sent = await locks.request(SEND_LOCK, { ifAvailable: true }, async (lock) => {
      if (lock === null) return false;
      await run();
      return true;
    });

    if (!sent) scheduleFlush();
  }

  async function flushOnce(): Promise<void> {
    if (signedOut) return;

    const batch = await takeBatch(db, { maxOps, maxBytes });
    if (batch.ops.length === 0) {
      await settleRollback();
      await idle();
      return;
    }

    setStatus({ kind: 'saving' });

    try {
      await sendWithSplit(batch.ops, batch.keys);
      attempt = 0;
    } catch (error) {
      if (isResyncRequired(error)) {
        await bootstrapInternal();
        return;
      }
      // Мёртвая сессия — не временная ошибка. Транспорт уже попробовал
      // обновиться сам и получил от сервера «сессии нет»: повторять здесь
      // нечего, чинится это только входом. Пока это считалось обычной
      // ошибкой, индикатор показывал «оффлайн» при живом сервере и повторял
      // до бесконечности, а экран входа не появлялся вовсе.
      if (isAuthError(error)) {
        giveUpOnSession();
        return;
      }
      scheduleRetry(await queueLength(db), isUnreachable(error));
      return;
    }

    // В очереди могло остаться больше, чем влезло в пачку.
    if ((await queueLength(db)) > 0) {
      await flushOnce();
      return;
    }

    await settleRollback();
    await idle();
  }

  /**
   * Забрать снимок, если что-то откатывали: у сервера правда о тех записях,
   * чью нашу версию он не принял.
   *
   * Флаг снимается **после** удачного снимка, а не до попытки. Пока он
   * снимался заранее, неудачный bootstrap — обычный 5xx или обрыв — оставлял
   * запись откаченной навсегда: `pull` её не вернёт, он отдаёт только
   * изменившееся с курсора, а второй попытки не было.
   */
  async function settleRollback(): Promise<void> {
    if (!rollbackNeeded || signedOut) return;

    try {
      await bootstrapInternal();
      rollbackNeeded = false;
    } catch {
      // Не вышло — флаг остаётся, и его подберёт следующая отправка или
      // ближайший опрос.
    }
  }

  async function flush(): Promise<void> {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
      debounceTimer = undefined;
    }
    return serialize(() => withSendLock(flushOnce));
  }

  // ------------------------------------------------------------------------
  // Приём
  // ------------------------------------------------------------------------

  async function absorb(entity: Entity, record: SyncRecord): Promise<MergeResult> {
    // Часы догоняют чужие: иначе наша следующая правка окажется «раньше»
    // только что полученной и молча проиграет ей при слиянии.
    for (const value of Object.values(record.field_clocks ?? {})) {
      clock.receive(value);
    }
    return mergeIncoming(db, entity, record);
  }

  async function pullOnce(): Promise<void> {
    const changed: MergeResult[] = [];

    for (;;) {
      const response = await transport.pull({ since: cursor, limit: DEFAULTS.pullLimit });

      for (const change of response.changes) {
        changed.push(await absorb(change.entity, change.record as SyncRecord));
      }

      cursor = response.cursor;
      await writeMeta(db, META_CURSOR, cursor);

      if (!response.has_more) break;
    }

    if (changed.length > 0) onChange?.(changed);
  }

  async function pullInternal(): Promise<void> {
    if (signedOut) return;

    // Откат, которому не хватило снимка, доигрывается здесь: без этого запись
    // оставалась бы откаченной до следующей отправки, а её может не быть.
    await settleRollback();

    try {
      await pullOnce();
    } catch (error) {
      if (isResyncRequired(error)) {
        await bootstrapInternal();
        return;
      }
      if (isAuthError(error)) {
        giveUpOnSession();
        return;
      }

      // Чужие правки не срочные: следующий звоночек, фокус или опрос заберут их.
      // Ронять из-за этого статус «сохранено» нельзя — своя очередь-то пуста.
      if ((await queueLength(db)) > 0) {
        scheduleRetry(await queueLength(db), isUnreachable(error));
      }
    }
  }

  async function bootstrapInternal(): Promise<void> {
    const response = await transport.bootstrap();
    const changed: MergeResult[] = [];

    const groups: [Entity, unknown[]][] = [
      ['task', response.snapshot.tasks],
      ['checklist_item', response.snapshot.checklist_items],
      ['task_link', response.snapshot.task_links],
      ['task_tag', response.snapshot.task_tags],
      ['settings', response.snapshot.settings === null ? [] : [response.snapshot.settings]],
    ];

    for (const [entity, records] of groups) {
      for (const record of records) {
        if (record === undefined || record === null) continue;
        changed.push(await absorb(entity, record as SyncRecord));
      }
    }

    // Снимок именно сливается, а не подменяет копию: неотправленные правки
    // никуда не делись и обязаны пережить bootstrap.
    cursor = response.cursor;
    await writeMeta(db, META_CURSOR, cursor);

    if (changed.length > 0) onChange?.(changed);
    await idle();
  }

  // ------------------------------------------------------------------------
  // Публичное
  // ------------------------------------------------------------------------

  async function mutate(input: MutationInput): Promise<MergeResult> {
    const mutation: Mutation = {
      op_id: crypto.randomUUID(),
      hlc: clock.now(),
      device_id: deviceId,
      source: input.source ?? 'human',
      entity: input.entity,
      entity_id: input.entityId,
      kind: input.kind,
      fields: input.fields ?? {},
    };

    const result = await applyLocalMutation(db, mutation, input.seed);
    onChange?.([result]);

    // Очередь только что пополнилась, и с этого мгновения «сохранено» — враньё.
    // Раньше статус трогал только `flushOnce`, то есть четыреста миллисекунд
    // паузы индикатор показывал галочку при непустой очереди. Заодно
    // обновляется счётчик ожидающих: он пересчитывался лишь при очередном
    // отказе, и «оффлайн, 1 правка ждёт» висело, когда их уже пять.
    if (status.kind === 'offline' || status.kind === 'error') {
      setStatus({ ...status, pending: await queueLength(db) });
    } else if (status.kind !== 'saving' && status.kind !== 'signed-out') {
      setStatus({ kind: 'saving' });
    }

    scheduleFlush();
    return result;
  }

  return {
    async start(): Promise<void> {
      stopped = false;
      // Вошли заново — значит сессия снова есть, и запрет на повторы снимается.
      signedOut = false;
      cursor = (await readMeta<number>(db, META_CURSOR)) ?? 0;
      lease = await readMeta<NumberLease>(db, META_NUMBERS);

      await serialize(async () => {
        try {
          // Курсор ноль означает «копии ещё нет»: pull с нуля отдал бы весь
          // журнал по одной записи, bootstrap — тот же результат одним запросом.
          if (cursor === 0) {
            await bootstrapInternal();
          } else {
            await pullInternal();
          }
        } catch {
          // Запуск без сети — штатный случай: данные показываются из локальной
          // копии, а догнать чужие правки поможет первый же удачный опрос.
          // Падать здесь значит не завести ни опрос, ни обработчики возврата
          // в сеть — то есть остаться оффлайн навсегда.
        }
      });

      // Блок номеров нужен до первой задачи, а не после: `number` у задачи
      // обязателен, и без блока завести её нечем (3.6.3 требований). Пустая
      // пачка с `want_numbers` — самый дешёвый способ его попросить.
      if (lease === undefined) {
        await serialize(async () => {
          try {
            await sendBatch([], []);
          } catch {
            // Нет сети — номера приедут с первой же удачной отправкой.
          }
        });
      }

      pollTimer = setInterval(() => {
        void serialize(pullInternal);
      }, pollIntervalMs);

      // Резерв на случай, когда сокет не поднимается вовсе: корпоративный
      // прокси, спящий телефон (раздел 5.2 архитектуры).
      globalThis.addEventListener?.('online', onOnline);
      globalThis.addEventListener?.('focus', onFocus);

      await flush();
    },

    stop(): void {
      stopped = true;
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      if (pollTimer !== undefined) clearInterval(pollTimer);
      debounceTimer = undefined;
      retryTimer = undefined;
      pollTimer = undefined;

      globalThis.removeEventListener?.('online', onOnline);
      globalThis.removeEventListener?.('focus', onFocus);
    },

    mutate,
    flush,

    pending(): Promise<number> {
      return queueLength(db);
    },

    pull(): Promise<void> {
      return serialize(pullInternal);
    },

    bump(seq: number): void {
      if (seq <= cursor) return;
      void serialize(pullInternal);
    },

    bootstrap(): Promise<void> {
      return serialize(bootstrapInternal);
    },

    status: () => status,
    cursor: () => cursor,

    rejected(): Promise<QuarantinedMutation[]> {
      return readQuarantine(db);
    },

    async forgetRejected(): Promise<void> {
      await forgetQuarantine(db);
      // Индикатор пересчитывается сразу: он и был единственным следом этих
      // правок на экране, и оставить его висеть после «выбросить» значит
      // показывать состояние, которого больше нет.
      await idle();
    },

    async allocateNumber(): Promise<number | undefined> {
      // Номер берётся транзакцией над базой, а не из копии блока в памяти:
      // копия у каждой вкладки своя, и две вкладки одного origin выдавали один
      // и тот же номер. Сервер такую задачу отвергает как дубль.
      const number = await takeNumber(db);

      lease = await readMeta<NumberLease>(db, META_NUMBERS);

      if (number === undefined) {
        // Блок кончился: попросим следующий ближайшей же отправкой.
        scheduleFlush();
      }

      return number;
    },
  };

  function onOnline(): void {
    attempt = 0;
    void flush();
    void serialize(pullInternal);
  }

  function onFocus(): void {
    void serialize(pullInternal);
  }
}
